import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { isDevMode } from '../utils/devMode'
import {
  AccessTokenContext,
  IdTokenClaimsContext,
  PortalAuthContext,
  PortalIdTokenContext,
  type PortalAuthState,
} from '../auth/authContexts'
import { AUTH0_SCOPE, getAuth0RedirectUri } from '../auth0Config'
import { readAuth0IdTokenFromCache } from '../auth/auth0IdToken'
import { storeAuthReturnTo } from '../auth/authReturnTo'
import { PORTAL_MFA_ACR_VALUES } from '../utils/portalMfa'
import { PORTAL_LOGOUT_RETURN_URL } from '../utils/portalMarketingRedirect'
import { markPortalReturningUser } from '../utils/portalReturningUser'
import { api } from '../services/api'

const DEV_PORTAL_AUTH: PortalAuthState = {
  isLoading: false,
  isAuthenticated: false,
  loginWithRedirect: async () => {},
  loginWithMfaEnrollment: async () => {},
  logout: async () => {},
}

async function devNoToken(): Promise<string | null> {
  return null
}

function ProductionAuthBridge({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect: auth0Login,
    logout: auth0Logout,
    getAccessTokenSilently,
    getIdTokenClaims: auth0GetIdTokenClaims,
    error: authError,
  } = useAuth0()

  useEffect(() => {
    if (isAuthenticated) {
      markPortalReturningUser()
    }
  }, [isAuthenticated])

  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim() || undefined

  const loginWithRedirect = useCallback(
    async (opts?: { signup?: boolean }) => {
      await auth0Login({
        authorizationParams: {
          redirect_uri: getAuth0RedirectUri(),
          ...(audience ? { audience } : {}),
          scope: AUTH0_SCOPE,
          ...(opts?.signup ? { screen_hint: 'signup' as const } : {}),
        },
      })
    },
    [auth0Login, audience]
  )

  const loginWithMfaEnrollment = useCallback(
    async (opts?: { returnTo?: string; forceLogin?: boolean }) => {
      // Default: keep SSO so Auth0 can show SMS MFA only (step-up). forceLogin adds
      // prompt=login (password again) — use only if SSO skips the challenge.
      await auth0Login({
        authorizationParams: {
          redirect_uri: getAuth0RedirectUri(),
          ...(audience ? { audience } : {}),
          scope: AUTH0_SCOPE,
          acr_values: PORTAL_MFA_ACR_VALUES,
          ...(opts?.forceLogin ? { prompt: 'login' as const } : {}),
        },
        appState: {
          returnTo: opts?.returnTo ?? window.location.pathname + window.location.search,
        },
      })
    },
    [auth0Login, audience]
  )

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated) return null

    const explicitParams = {
      authorizationParams: {
        ...(audience ? { audience } : {}),
        scope: AUTH0_SCOPE,
      },
    }

    const delaysMs = [0, 30, 80, 150, 250, 400]

    let lastError: unknown
    for (let i = 0; i < delaysMs.length; i++) {
      if (delaysMs[i] > 0) {
        await new Promise((r) => setTimeout(r, delaysMs[i]))
      }
      try {
        // Right after redirect, cache keys match Auth0Provider defaults — prefer no-arg first.
        const token =
          i === 0
            ? await getAccessTokenSilently()
            : await getAccessTokenSilently(explicitParams)
        if (token) return token
      } catch (error) {
        lastError = error
      }
    }

    console.error('[Auth] getAccessTokenSilently failed after retries:', lastError)
    return null
  }, [getAccessTokenSilently, isAuthenticated, audience])

  const getIdTokenClaims = useCallback(async (): Promise<Record<string, unknown> | undefined> => {
    if (!isAuthenticated) return undefined
    try {
      const claims = await auth0GetIdTokenClaims()
      return claims ?? undefined
    } catch (error) {
      console.error('[Auth] getIdTokenClaims failed:', error)
      return undefined
    }
  }, [auth0GetIdTokenClaims, isAuthenticated])

  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.trim()

  const getPortalIdToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated || !clientId) return null
    return readAuth0IdTokenFromCache(clientId)
  }, [clientId, isAuthenticated])

  const logout = useCallback(
    async (opts?: { returnTo?: string }) => {
      markPortalReturningUser()
      const { clearPortalCsrfToken } = await import('../utils/portalCsrfStore')
      try {
        await api.portalLogout(getAccessToken)
      } catch (err) {
        console.error('[Auth] server logout failed (continuing with Auth0 logout):', err)
      }
      clearPortalCsrfToken()
      await auth0Logout({
        logoutParams: { returnTo: opts?.returnTo ?? PORTAL_LOGOUT_RETURN_URL },
      })
    },
    [auth0Logout, getAccessToken],
  )

  const portalValue = useMemo(
    (): PortalAuthState => ({
      isLoading,
      isAuthenticated,
      loginWithRedirect,
      loginWithMfaEnrollment,
      logout,
      authError,
    }),
    [isLoading, isAuthenticated, loginWithRedirect, loginWithMfaEnrollment, logout, authError]
  )

  return (
    <PortalAuthContext.Provider value={portalValue}>
      <AccessTokenContext.Provider value={getAccessToken}>
        <IdTokenClaimsContext.Provider value={getIdTokenClaims}>
          <PortalIdTokenContext.Provider value={getPortalIdToken}>
            {children}
          </PortalIdTokenContext.Provider>
        </IdTokenClaimsContext.Provider>
      </AccessTokenContext.Provider>
    </PortalAuthContext.Provider>
  )
}

interface AuthProviderProps {
  children: ReactNode
}

export default function AuthProvider({ children }: AuthProviderProps) {
  useLayoutEffect(() => {
    if (isDevMode()) {
      const url = new URL(window.location.href)
      if (
        url.searchParams.has('code') ||
        url.searchParams.has('state') ||
        url.hash.includes('code=')
      ) {
        window.history.replaceState({}, '', url.pathname || '/')
      }
    }
  }, [])

  if (isDevMode()) {
    return (
      <PortalAuthContext.Provider value={DEV_PORTAL_AUTH}>
        <AccessTokenContext.Provider value={devNoToken}>{children}</AccessTokenContext.Provider>
      </PortalAuthContext.Provider>
    )
  }

  const domain = import.meta.env.VITE_AUTH0_DOMAIN?.trim()
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.trim()
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim() || undefined

  if (!domain || !clientId) {
    return (
      <div className="min-h-screen bg-background-brand-primary flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-background-primary rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-text-primary-900 mb-4">
            Authentication configuration error
          </h2>
          <p className="text-text-tertiary-600 mb-2">
            Missing Auth0 build configuration. Set{' '}
            <span className="font-mono text-sm">VITE_AUTH0_DOMAIN</span> and{' '}
            <span className="font-mono text-sm">VITE_AUTH0_CLIENT_ID</span> for production builds.
          </p>
          <p className="text-sm text-text-tertiary-500">
            Optional: <span className="font-mono">VITE_AUTH0_AUDIENCE</span> must match your Auth0 API
            identifier when the API validates access tokens.
          </p>
        </div>
      </div>
    )
  }

  // useRefreshTokens={true} requires Refresh Token Rotation on the Auth0 SPA app + tenant; otherwise
  // getAccessTokenSilently fails with "Missing Refresh Token" after login.
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: getAuth0RedirectUri(),
        ...(audience ? { audience } : {}),
        scope: AUTH0_SCOPE,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={false}
      onRedirectCallback={(appState) => {
        if (appState && typeof appState === 'object' && 'returnTo' in appState) {
          storeAuthReturnTo((appState as { returnTo?: unknown }).returnTo)
        }
      }}
    >
      <ProductionAuthBridge>{children}</ProductionAuthBridge>
    </Auth0Provider>
  )
}
